<template>
    <desktop-panel-wrapper v-if="mode === 'list'">
        <panel-heading @close="cancel" :title="$t('IMAGE_IMAGES')" />

        <div class="cz2__image-list lg:hidden">
            <button
                class="cz2__image-button plus-icon cz2__image-button--with-text"
                :aria-label="$t('IMAGE_ADD')" @click.prevent="listAddImage">
                <span>{{ $t('IMAGE_ADD') }}</span>
            </button>

            <button
                v-for="image in filledImagePlacements" :key="image.placement.code"
                class="cz2__image-button cz2__image-button--with-text cz2__image-button--upload"
                @click.prevent="listEditImage(image)">
                <span class="cz2__image-editor__small-preview">
                    <span
                        :style="{
                            backgroundImage: makeImageUrl(image.component.custom['dynamic-image-url'] + '?fmt=jpg&qlt=70&rwid=100'),
                        }"></span>
                </span>

                <span>{{ image.component.custom['dynamic-image-original-name'] }}</span>
            </button>
        </div>

        <div class="cz2__image-list hidden lg:!block">
            <div class="cz2__image-list-action cz__tour__list-add">
                <div class="cz2__image-list-action__add-icon" @click.prevent="listAddImage">
                </div>
                <div class="cz2__image-list-action__text text-sm lg:text-base text-black" @click.prevent="listAddImage">
                    {{ $t('IMAGE_ADD') }}
                </div>
                <div class="cz2__image-list-action__actions">
                    <button
                        class="cz2__image-button plus-icon" :aria-label="$t('IMAGE_ADD')"
                        @click.prevent="listAddImage"></button>
                </div>
            </div>

            <div v-for="(image, index) in filledImagePlacements" :key="image.placement.code" class="cz2__image-list-action">
                <div class="cz2__image-list-action__preview">
                    <span
                        :style="{
                            backgroundImage: 'url(' + image.component.custom['dynamic-image-url'] + '?fmt=jpg&qlt=70&rwid=100' + ')'
                        }"></span>
                </div>
                <div class="cz2__image-list-action__text text-sm lg:text-base text-black">
                    {{ image.component.custom['dynamic-image-original-name'] }}
                </div>
                <div
                    class="flex"
                    :class="[
                        index === 0 ? 'cz__tour__list-edit' : null,
                    ]"
                >

                    <button
                        class="cz2__image-button"
                        :aria-label="$t('IMAGE_EDIT_EDIT')" @click.prevent="listEditImage(image)">
                        <div class="mt-0.5">Edit</div>
                    </button>
                    <button
                        class="cz2__image-button cz2__image-button--remove cz2__ml-10" :aria-label="$t('IMAGE_EDIT_REMOVE')"
                        @click.prevent="listRemoveImage(image)"></button>
                </div>
            </div>
        </div>

        <div class="flex flex-col flex-1" />
        <action-buttons-wrapper>
            <secondary-button
                tour-class="cz__tour__list-back"
                @click="accept"
            >
                {{ $t('MENU_BACK') }}
            </secondary-button>
        </action-buttons-wrapper>
    </desktop-panel-wrapper>

    <desktop-panel-wrapper v-else-if="mode === 'gallery'">
        <!-- Gallery list -->
        <button class="back-button hidden lg:!block">
            {{ $t('IMAGE_EDIT_COLOR_CHANGE') }}
        </button>

        <div class="mb-4 lg:hidden">
            <div class="text-base font-bold text-center">
                {{ $t('IMAGE_EDIT_COLOR_CHANGE') }}
            </div>
        </div>

        <div v-for="group in availableGalleryImages" :key="group.category" class="cz2__gallery-upload__category">
            <div class="text-sm">{{ group.category }}</div>

            <div class="cz2__gallery-upload__images">
                <button
                    v-for="image in group.images" :key="image.url"
                    :class="[
                        'cz2__gallery-upload__image',
                        {
                            'cz2__gallery-upload__image--selected': image === selectedGalleryImage,
                        }
                    ]" :title="image.name"
                    @click.prevent="selectGalleryImage(image)">
                    <span
                        class="cz2__gallery-upload__image__image" :data-url="image.url"
                        :style="{
                            backgroundImage: makeImageUrl(image.url)
                        }"></span>
                </button>
            </div>
        </div>
        <div class="flex flex-col flex-1" />
        <action-buttons-wrapper>
            <secondary-button
                @click="cancelGallery">
                {{ $t('ACTION_CANCEL') }}
            </secondary-button>
            <primary-button @click="acceptGallery">
                {{ $t('ACTION_SAVE') }}
            </primary-button>
        </action-buttons-wrapper>
    </desktop-panel-wrapper>

    <desktop-panel-wrapper v-else-if=" mode === 'edit'">
        <panel-heading :title="$t('IMAGE_EDIT')" @close="cancel" />

        <div
            :class="[
                'cz2__image-editor',
                'cz__tour__edit-options',
            ]">
            <div v-if="editMode == null" class="cz2__image-editor__field lg:hidden">
                <div class="cz2__image-editor__field-name">
                    <template v-if="editComponent.custom['gallery-image'] == 'true'">
                        <button class="cz2__image-button cz2__image-button--with-text cz2__image-button--upload">
                            <span class="cz2__image-editor__small-preview">
                                <span
                                    :style="{
                                        backgroundImage: 'url(' + editComponent.custom['dynamic-image-url'] + '?fmt=jpg&qlt=70&rwid=100' + ')',
                                    }"></span>
                            </span>

                            <span>{{ $t('IMAGE_EDIT_GRAPHIC') }}</span>
                        </button>
                    </template>
                    <template v-if="editComponent.custom['gallery-image'] == 'false'">
                        <button
                            class="cz2__image-button cz2__image-button--with-text cz2__image-button--upload"
                            @click.prevent="changeImage">
                            <span class="cz2__image-editor__small-preview">
                                <span
                                    :style="{
                                        backgroundImage: 'url(' + editComponent.custom['dynamic-image-url'] + '?fmt=jpg&qlt=70&rwid=100' + ')',
                                    }"></span>
                            </span>

                            <span>{{ $t('IMAGE_EDIT_CHANGE') }}</span>
                        </button>
                    </template>
                </div>
            </div>

            <div v-if="editMode == null" class="cz2__image-editor__field hide--below-sm">
                <div class="cz2__image-editor__field-name text-sm">
                    <span v-if="editComponent.custom['gallery-image'] == 'true'">
                        {{ $t('IMAGE_EDIT_GRAPHIC') }}
                    </span>
                    <span v-else>
                        {{ $t('IMAGE_EDIT_FILE') }}
                    </span>
                </div>
                <div class="cz2__image-editor__field-value">
                    <template v-if="editComponent.custom['gallery-image'] === 'true'">
                        <span class="cz2__image-editor__preview">
                            <span
                                :style="{
                                    backgroundImage: 'url(' + editComponent.custom['dynamic-image-url'] + '?fmt=jpg&qlt=70&rwid=100' + ')',
                                }"></span>
                        </span>
                    </template>
                    <template v-if="editComponent.custom['gallery-image'] !== 'true'">
                        <span class="cz2__button-like">
                            {{ editComponent.custom['dynamic-image-original-name'] }}
                        </span>

                        <input
                            ref="uploaderEditSource" type="file"
                            class="cz2__image-editor__uploader-input"
                            :accept="mimeTypes.join(',')" @change="editUploaderPicked($event)" />

                        <button class="cz2__ml-20 inline cz2__link-button" @click.prevent="changeImage">
                            {{ $t('IMAGE_EDIT_CHANGE') }}
                        </button>
                    </template>
                </div>
            </div>

            <div
                v-if="canEditColor && (editMode == null || editMode === 'color')" :class="[
                    'cz2__text-editor__field',
                    {
                        'cz2__text-editor__field--expanded': editMode === 'color',
                    }
                ]">
                <div class="cz2__text-editor__field-name text-sm">
                    <button
                        class="cz2__image-button cz2__image-button--with-text cz2__image-button--color cz2__text-editor__color-button"
                        @click.prevent="editSmallColor">
                        <span class="cz2__text-editor__color-button__swatch" :style="selectedColorStyle"></span>

                        <span>{{ $t('IMAGE_EDIT_COLOR') }}</span>
                    </button>

                    <span>{{ $t('IMAGE_EDIT_COLOR') }}</span>
                </div>
                <div class="cz2__text-editor__field-value">
                    <div class="cz2__colors__options">
                        <button
                            v-for="color in primaryColors" :key="color.code"
                            :class="[
                                'cz2__color-option',
                                {
                                    'cz2__color-option--selected': selectedColor && selectedColor.hex === color.custom.hex,
                                    'cz2__color-option--border': color.custom && color.custom['v-border'] === 'true',
                                }
                            ]" @click.prevent="pickColor(color)"
                            :aria-label="color.name">
                            <span class="cz2__color-option__color" :style="getColorStyle(color)">
                            </span>
                        </button>

                        <button class="cz2__color-more" @click.prevent="editColor">
                            <span class="more-icon">
                            </span>

                            <span class="cz2__color-more__text">{{ $t('COLOR_MORE') }}</span>
                        </button>
                    </div>
                </div>
            </div>

            <div
                v-if="editMode == null || editMode === 'alignment'" :class="[
                    'cz2__image-editor__field',
                    {
                        'cz2__image-editor__field--expanded': editMode === 'alignment',
                    }
                ]">
                <div class="cz2__image-editor__field-name text-sm">
                    <button
                        class="cz2__image-button cz2__image-button--with-text cz2__image-button--align-center"
                        @click.prevent="editAlignment">
                        <span>{{ $t('IMAGE_EDIT_ALIGNMENT') }}</span>
                    </button>

                    <span>{{ $t('IMAGE_EDIT_ALIGNMENT') }}</span>
                </div>
                <div class="cz2__image-editor__field-value">
                    <button
                        class="cz2__image-button cz2__image-button--align-left"
                        :aria-label="$t('IMAGE_EDIT_ALIGN_LEFT')" @click.prevent="alignLeft">
                    </button>

                    <button
                        class="cz2__image-button cz2__image-button--align-center cz2__ml-10"
                        :aria-label="$t('IMAGE_EDIT_ALIGN_CENTER')" @click.prevent="alignCenter">
                    </button>

                    <button
                        class="cz2__image-button cz2__image-button--align-right cz2__ml-10"
                        :aria-label="$t('IMAGE_EDIT_ALIGN_RIGHT')" @click.prevent="alignRight">
                    </button>

                    <button
                        class="cz2__image-button cz2__image-button--align-top cz2__ml-10"
                        :aria-label="$t('IMAGE_EDIT_ALIGN_TOP')" @click.prevent="alignTop">
                    </button>

                    <button
                        class="cz2__image-button cz2__image-button--align-v-center cz2__ml-10"
                        :aria-label="$t('IMAGE_EDIT_ALIGN_VCENTER')" @click.prevent="alignVCenter">
                    </button>

                    <button
                        class="cz2__image-button cz2__image-button--align-bottom cz2__ml-10"
                        :aria-label="$t('IMAGE_EDIT_ALIGN_BOTTOM')" @click.prevent="alignBottom">
                    </button>
                </div>
            </div>

            <div
                v-if="isPrintingMode && (editMode == null || editMode === 'layering')" :class="[
                    'cz2__image-editor__field',
                    {
                        'cz2__image-editor__field--expanded': editMode === 'layering',
                    }
                ]">
                <div class="cz2__image-editor__field-name text-sm">
                    <button
                        class="cz2__image-button cz2__image-button--with-text cz2__image-button--layer-any"
                        @click.prevent="editLayering">
                        <span>{{ $t('IMAGE_EDIT_LAYERING') }}</span>
                    </button>

                    <span>{{ $t('IMAGE_EDIT_LAYERING') }}</span>
                </div>
                <div class="cz2__image-editor__field-value">
                    <button
                        class="cz2__image-button cz2__image-button--layer-down"
                        :aria-label="$t('IMAGE_EDIT_LAYER_DOWN')" @click.prevent="layerDown">
                    </button>

                    <button
                        class="cz2__image-button cz2__image-button--layer-up cz2__ml-10"
                        :aria-label="$t('IMAGE_EDIT_LAYER_UP')" @click.prevent="layerUp">
                    </button>
                </div>
            </div>

            <div v-if="editMode == null" class="cz2__image-editor__field">
                <div class="cz2__image-editor__field-name text-sm">
                    <button
                        class="cz2__image-button cz2__image-button--with-text cz2__image-button--remove"
                        @click.prevent="removeImage">
                        <span>{{ $t('IMAGE_EDIT_REMOVE') }}</span>
                    </button>

                    <span>{{ $t('IMAGE_EDIT_REMOVE') }}</span>
                </div>
                <div class="cz2__image-editor__field-value">
                    <button
                        class="cz2__image-button cz2__image-button--remove" :aria-label="$t('IMAGE_EDIT_REMOVE')"
                        @click.prevent="removeImage">
                    </button>
                </div>
            </div>
        </div>

        <div class="flex flex-col flex-1" />
        <action-buttons-wrapper>
            <secondary-button
                @click="cancelEdit">
                {{ $t('ACTION_CANCEL') }}
            </secondary-button>
            <primary-button @click="saveEdit">
                {{ $t('ACTION_SAVE') }}
            </primary-button>
        </action-buttons-wrapper>
    </desktop-panel-wrapper>

    <desktop-panel-wrapper v-else-if="mode === 'upload'">
        <template v-if="uploadWorking">
            <div class="cz2__full-uploader__progress">
                <div class="cz2__full-uploader__current" :style="uploadProgressStyle()">
                </div>
            </div>
        </template>

        <div
            :class="[
                'cz2__image__small-upload',
                'lg:hidden',
                {
                    'cz2__image__small-upload--working': uploadWorking
                }
            ]">
            <panel-heading @close="cancel" :title="$t('IMAGE_ADD')" />

            <div class="cz2__image__small-upload__items">
                <button
                    class="cz2__image-button cz2__image-button--with-text cz2__image-button--gallery"
                    @click.prevent="pickGallery">
                    <span>{{ $t('IMAGE_UPLOAD_SMALL_GRAPHIC_GALLERY') }}</span>
                </button>

                <button
                    class="cz2__image-button cz2__image-button--with-text cz2__image-button--upload"
                    @click.prevent="pickFile">
                    <span>{{ $t('IMAGE_UPLOAD_SMALL_UPLOAD') }}</span>
                </button>
            </div>
        </div>

        <div class="hidden lg:!block overflow-y-auto">
            <panel-heading @close="cancel" :title="$t('IMAGE_ADD')" />

            <div
                :class="[
                    'cz2__image__upload overflow-x-hidden',
                    {
                        'cz2__image__upload--working': uploadWorking
                    }
                ]">
                <div class="cz2__tabs">
                    <button
                        :class="[
                            'cz2__tabs__button',
                            {
                                'cz2__tabs__button--selected': uploadMode === 'image',
                            }
                        ]" @click.prevent="selectUploadMode('image')">{{ $t('IMAGE_UPLOAD_IMAGE') }}</button>

                    <button
                        :class="[
                            'cz2__tabs__button',
                            {
                                'cz2__tabs__button--selected': uploadMode === 'gallery',
                            }
                        ]" @click.prevent="selectUploadMode('gallery')">{{ $t('IMAGE_UPLOAD_GALLERY') }}</button>
                </div>

                <div v-if="uploadMode === 'gallery'" class="cz2__gallery-upload">
                    <div
                        v-for="group in availableGalleryImages" :key="group.category"
                        class="cz2__gallery-upload__category">
                        <div class="text-sm">{{ group.category }}</div>

                        <div class="cz2__gallery-upload__images">
                            <button
                                v-for="image in group.images" :key="image.url"
                                :class="[
                                    'cz2__gallery-upload__image',
                                    {
                                        'cz2__gallery-upload__image--selected': image === selectedGalleryImage,
                                    }
                                ]" :title="image.name"
                                @click.prevent="selectGalleryImage(image)">
                                <span
                                    class="cz2__gallery-upload__image__image" :data-url="image.url"
                                    :style="{
                                        backgroundImage: makeImageUrl(image.url),
                                    }"></span>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="uploadMode === 'image'" class="cz2__image-upload">
                    <div v-if="personalizationMethod === 'etching'" class="text-sm cz2__image-upload__etching-warning">
                        {{ $t('IMAGE_UPLOAD_ETCHING') }}
                    </div>

                    <div v-if="personalizationMethod === 'printing'" class="text-sm cz2__image-upload__etching-warning">
                        {{ $t('IMAGE_UPLOAD_PRINTING') }}
                    </div>

                    <div
                        :class="[
                            'cz2__image-upload__full-uploader',
                            {
                                'cz2__image-upload__full-uploader--over': uploadDraggingOver,
                            }
                        ]">
                        <div
                            class="cz2__full-uploader__drop-zone" @dragenter="uploaderDragEnter($event)"
                            @dragleave="uploaderDragLeave($event)" @drop="uploaderDrop($event)"
                            @dragover="uploaderDragOver($event)" @click.prevent="pickFile">
                            <span class="cz2__full-uploader__drop-icon">
                            </span>

                            <span class="cz2__full-uploader__drop-text text-sm">
                                {{ $t('IMAGE_UPLOAD_DROP') }}
                            </span>
                        </div>

                        <input
                            ref="uploaderFullSource" type="file"
                            class="cz2__full-uploader__input"
                            :accept="mimeTypes.join(',')" @change="uploaderPicked($event)" />
                    </div>
                </div>
            </div>
        </div>
        <div class="flex flex-col flex-1" />
        <div class="px-1 mb-2" v-if="uploadMode !== 'gallery'">
            <primary-button @click.prevent="pickFile">
                {{ $t('IMAGE_UPLOAD_UPLOAD_IMAGE') }}
            </primary-button>
        </div>
        <action-buttons-wrapper>
            <secondary-button
                @click="uploadMode === 'gallery' ? cancelGallery() : cancelImageUpload()">
                {{ $t('ACTION_CANCEL') }}
            </secondary-button>
            <primary-button @click="uploadGalleryImage" v-if="!!(uploadMode === 'gallery' && selectedGalleryImage)">
                {{ $t('ACTION_SAVE') }}
            </primary-button>
        </action-buttons-wrapper>
    </desktop-panel-wrapper>

    <desktop-panel-wrapper v-else-if="mode === 'color'">
        <panel-header
            :title="$t('IMAGE_EDIT_COLOR_CHANGE') + (selectedColor ? (' &mdash; ' + selectedColor.name) : '')" />

        <div class="cz2__colors__options cz2__colors__options--fullscreen stretch">
            <button
                v-for="color in availableColors" :key="color.code"
                :class="[
                    'cz2__color-option',
                    {
                        'cz2__color-option--selected': selectedColor ? selectedColor.hex === color.custom.hex : false,
                        'cz2__color-option--border': color.custom && color.custom['v-border'] === 'true',
                    }
                ]" @click.prevent="pickColor(color)"
                :aria-label="color.name">
                <span class="cz2__color-option__color" :style="getColorStyle(color)">
                </span>
            </button>
        </div>
        <div class="flex flex-col flex-1" />
        <action-buttons-wrapper>
            <secondary-button
                @click="cancelColor">
                {{ $t('ACTION_CANCEL') }}
            </secondary-button>
            <primary-button @click="acceptColor">
                {{ $t('ACTION_SAVE') }}
            </primary-button>
        </action-buttons-wrapper>
    </desktop-panel-wrapper>
</template>

<style lang="scss">
@import "../../style/variables.scss";

#cz2.cz2 {
    .cz2__image-list {
        // margin-top: 20px;

        @include respond-below(sm) {
            display: flex;
            justify-content: center;

            button {
                margin-left: 25px;
                margin-right: 25px;
            }
        }
    }

    .cz2__image-list-action {
        display: flex;
        align-items: center;

        height: $button-height;
        margin-bottom: 20px;
    }

    .cz2__image-list-action__add-icon {
        display: block;
        flex: 0 0 auto;

        width: $button-height;
        height: $button-height;

        margin-right: 12px;

        background: transparent url(../../assets/menu-images@2x.png) no-repeat center/contain;
    }

    .cz2__image-list-action__preview {
        position: relative;

        display: block;
        flex: 0 0 auto;

        width: $button-height;
        height: $button-height;

        border-radius: $button-height;

        margin-right: 12px;

        overflow: hidden;

        span {
            display: block;
            position: absolute;

            left: 0;
            top: 0;
            right: 0;
            bottom: 0;

            background-size: contain;
            background-position: center;
            background-repeat: no-repeat;
        }
    }

    .cz2__image-list-action__text {
        flex: 1 0 auto;
    }

    .cz2__image-list-action__actions {
        flex: 0 0 auto;
    }

    .cz2__image-editor {
        display: flex;
        flex-direction: row;
        justify-content: center;

        @include respond-above(sm) {
            display: block;

            margin-top: 20px;
        }

        &.cz2__image-editor--top-level {
            justify-content: left;

            overflow: scroll;
            -webkit-overflow-scrolling: touch;

            min-height: 85px;
            align-items: flex-start;

            @include respond-above(sm) {

                min-height: 0;
            }
        }
    }

    .cz2__image-editor__uploader-input {
        position: absolute;

        left: 0;
        top: 0;
        width: 100%;
        height: 100%;

        -webkit-appearance: none;
        appearance: none;

        visibility: hidden;

        cursor: pointer;

        opacity: 0;
    }

    .cz2__image-editor__field {
        display: flex;

        align-items: center;

        margin-bottom: 20px;
    }

    .cz2__image-editor__field--expanded {
        @include respond-below(sm) {
            .cz2__image-editor__field-name {
                display: none;
            }

            .cz2__image-editor__field-value {
                display: flex;

                width: 100%;
                margin-top: 15px;
            }
        }
    }

    // Oof. Had to defeat specificity...
    .cz2__image-button.cz2__image-button--with-text span.cz2__image-editor__small-preview {
        position: absolute;

        display: block;
        flex: 0 0 auto;

        width: $button-height;
        height: $button-height;
        top: 0;
        left: 50%;

        z-index: 1;

        transform: translate(-50%, 0);

        border-radius: $button-height;

        margin-right: 12px;

        overflow: hidden;

        background: #fff;

        span {
            display: block;
            position: absolute;

            left: 0;
            top: 0;
            right: 0;
            bottom: 0;

            background-size: contain;
            background-position: center;
            background-repeat: no-repeat;

            transform: none;
        }
    }

    .cz2__image-editor__preview {
        position: relative;

        display: block;
        flex: 0 0 auto;

        width: $button-height;
        height: $button-height;

        border-radius: 2px;
        border: 1px solid #f0f0f0;
        background-color: #ffffff;

        span {
            display: block;
            position: absolute;

            left: 8px;
            top: 8px;
            right: 8px;
            bottom: 8px;

            background-size: contain;
            background-position: center;
            background-repeat: no-repeat;
        }
    }

    .cz2__image-editor__field-name {
        flex: 0 0 auto;

        margin-left: 25px;
        margin-right: 25px;

        >span {
            display: none;
        }

        @include respond-above(sm) {
            margin-left: 0;
            margin-right: 0;

            width: 100px;

            >button {
                display: none;
            }

            >span {
                display: block;
            }
        }
    }

    .cz2__image-editor__field-value {
        display: none;

        flex: 1 0 auto;

        width: 1px;

        @include respond-above(sm) {
            display: block;
        }
    }

    .cz2__image__upload {
        margin-top: 20px;

        &.cz2__image__upload--working {
            opacity: 0.2;
        }
    }

    .cz2__image__small-upload__items {
        display: flex;

        justify-content: center;

        button+button {
            margin-left: 60px;
        }
    }

    .cz2__image-upload__etching-warning {
        margin-top: 20px;
    }

    .cz2__image-upload__full-uploader {
        position: relative;

        margin-top: 30px;
    }

    .cz2__full-uploader__drop-zone {
        display: flex;

        flex-direction: column;
        justify-content: center;
        align-items: center;

        height: 140px;

        border-radius: 5px;
        border: 1px dashed #b1b3b3;
        background-color: #ffffff;

        cursor: pointer;
    }

    .cz2__image-upload__full-uploader--over {
        .cz2__full-uploader__drop-zone {
            border: 1px solid #fff;
        }
    }

    .cz2__full-uploader__drop-icon {
        display: block;

        width: 16px;
        height: 20px;

        background: transparent url(../../assets/upload@2x.png) no-repeat center/contain;
    }

    .cz2__full-uploader__drop-text {
        margin-top: 25px;
    }

    .cz2__full-uploader__or {
        margin-top: 30px;
        margin-bottom: 30px;

        text-align: center;
    }

    .cz2__full-uploader__manual {
        button {
            width: 100%;
            height: $button-height;

            background: #000;

            color: #ffffff;
            font-family: $font-main;
            font-size: 18px;
            font-weight: 400;
            line-height: 44px;
        }
    }

    .cz2__full-uploader__input {
        position: absolute;

        left: 0;
        top: 0;
        width: 100%;
        height: 100%;

        -webkit-appearance: none;
        appearance: none;

        visibility: hidden;

        cursor: pointer;

        opacity: 0;
    }

    .cz2__gallery-upload {
        margin-top: 20px;
    }

    .cz2__gallery-upload__category {
        margin-top: 10px;
    }

    .cz2__gallery-upload__images {
        margin-top: 20px;

        margin-left: -5px;
        margin-right: -5px;
    }

    .cz2__gallery-upload__image {
        position: relative;

        margin-left: 5px;
        margin-right: 5px;
        margin-bottom: 10px;

        width: 60px;
        height: 60px;
        border-radius: 2px;
        border: 1px solid;
        border-color: #d6d3d1; // stone-300
        background-color: #fff;

        &.cz2__gallery-upload__image--selected {
            &:after {
                content: '';

                position: absolute;
                display: block;

                left: 0;
                top: 0;

                width: 100%;
                height: 100%;

                border: 2px solid #000;
            }
        }
    }

    .cz2__gallery-upload__image__image {
        position: absolute;

        left: 8px;
        top: 8px;
        right: 8px;
        bottom: 8px;

        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
    }

    .cz2__full-uploader__progress {
        position: absolute;

        left: calc(50% - 30px);
        top: calc(50% - 30px);

        width: 60px;
        height: 60px;

        border-radius: 100px;
        overflow: hidden;

        z-index: 100;

        background: #ccc;

        mask-image: radial-gradient(circle farthest-side at center, transparent 75%, white 75%);

        .cz2__full-uploader__current {
            position: absolute;

            left: 0;
            top: 0;

            width: 100%;
            height: 100%;
        }

        @include respond-above(sm) {
            top: calc(400px - 30px);
        }
    }
}
</style>

<script>
    import Vue from 'vue';
    import axios from 'axios';
    import { Fragment } from 'vue-frag';

    import { mapActions, mapState } from 'vuex';
    import { useMediaQuery } from '@vueuse/core';
    import PanelHeading from './PanelHeading.vue';
    import PanelHeader from '../PanelHeader.vue';

    import FullScreenPanel from './FullScreenPanel.vue';

    import * as constants from '../../constants';

    import designArea from '../../utilities/designArea';
    import extent from '../../utilities/extent';
    import FullScreenPanelContent from './FullScreenPanelContent.vue';
    import DesktopPanelWrapper from './desktop/DesktopPanelWrapper.vue';
    import PanelsWrapper from './PanelsWrapper.vue';
    import ActionButtonsWrapper from '../buttons/ActionButtonsWrapper.vue';
    import SecondaryButton from '../buttons/SecondaryButton.vue';
    import PrimaryButton from '../buttons/PrimaryButton.vue';

    import * as Tours from '../../tours';
    import { fillEmptyDivs } from '../../utils';

    export default {
        components: {
            PanelHeading,
            PanelHeader,
            FullScreenPanel,
            FullScreenPanelContent,
            DesktopPanelWrapper,
            PanelsWrapper,
            Fragment,
            ActionButtonsWrapper,
            SecondaryButton,
            PrimaryButton,
        },
        data() {
            return {
                mode: 'list',
                uploadMode: 'image',
                editMode: null,

                uploadDraggingOver: false,
                uploadWorking: false,
                uploadProgress: 0,

                selectedGalleryImage: null,

                editPlacement: null,
                editComponent: null,

                lastSelectedNonprimary: null,
            };
        },
        computed: {
            ...mapState({
                selectedStep: (state) => state.selectedStep,
            }),

            isPrintingMode() {
                return this.viewController.blueprint.custom['v-personalzation-type'] === 'printing';
            },

            isDesktop() {
                return useMediaQuery(`(min-width: ${constants.DESKTOP_BREAKPOINT}px)`);
            },

            /**
             * Current view controller.
             */
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            /**
             * Currently selected view.
             */
            currentView() {
                return this.$store.state.customizer.currentView;
            },

            /**
             * Gets a list of filled images.
             */
            filledImagePlacements() {
                return this.$store.getters.filledImagePlacements;
            },

            /**
             * Gets a list of text placements.
             */
            textPlacements() {
                return this.$store.getters.textPlacements;
            },

            /**
             * Gets a list of free images.
             */
            emptyImagePlacements() {
                return this.$store.getters.emptyImagePlacements;
            },

            /**
             * Current customization mode.
             */
            personalizationMethod() {
                return this.$store.state.customizer.personalizationMethod;
            },

            /**
             * Calculate MIME types supported by the personalization.
             *
             * Used by the Open File dialog.
             */
            mimeTypes() {
                if (this.personalizationMethod === 'printing') {
                    return ['image/tiff', 'image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf', '.ai'];
                }

                return ['image/tiff', 'image/png', 'image/svg+xml', 'application/pdf', '.ai'];
            },

            /**
             * Calculate MIME types supported by the personalization.
             *
             * Used for validation.
             */
            mimeTypesSelected() {
                if (this.personalizationMethod === 'printing') {
                    return ['image/tiff', 'image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf', 'application/postscript'];
                }

                return ['image/tiff', 'image/png', 'image/svg+xml', 'application/pdf', 'application/postscript'];
            },

            /**
             * Can users edit color of images.
             */
            canEditColor() {
                let gallerySelected = false;
                let allowColors = true;

                if (this.editPlacement) {
                    const selected = this.viewController.selectedAtPlacement(this.editPlacement.code);

                    if (selected && selected.custom && selected.custom['gallery-image'] === 'true') {
                        gallerySelected = true;

                        if ((selected.custom['gallery-flags'] || '').includes('N')) {
                            allowColors = false;
                        }
                    }
                }

                return gallerySelected && allowColors && this.personalizationMethod !== 'etching';
            },

            /**
             * Checks whether image represents a gallery image.
             */
            gallerySelected() {
                if (this.editPlacement) {
                    const selected = this.viewController.selectedAtPlacement(this.editPlacement.code);

                    if (selected && selected.custom && selected.custom['gallery-image'] === 'true') {
                        return true;
                    }
                }

                return false;
            },

            availableGalleryImages() {
                if (this.viewController == null) {
                    return [];
                }

                const images = [];

                const galleryPlacement = this.viewController.findPlacement(constants.PLACEMENT_IMAGE_GALLERY);

                const names = {};

                // Collect names from covers.
                galleryPlacement.components.forEach((image) => {
                    if (image.custom?.cover) {
                        const category = image.custom?.category;
                        const name = this.viewController.componentNames(image).join('');

                        if (category && name) {
                            names[category] = name;
                        }
                    }
                });

                galleryPlacement.components.forEach((image) => {
                    if (image.custom?.cover) {
                        return;
                    }

                    const category = names[image.custom?.category] ?? image.custom?.category;

                    if (this.personalizationMethod === 'etching') {
                        if (image.custom?.['v-no-etching']) {
                            return;
                        }
                    }

                    if (this.personalizationMethod === 'printing') {
                        if (image.custom?.['v-no-printing']) {
                            return;
                        }
                    }

                    let list = images.find((i) => i.category === category);

                    if (list == null) {
                        list = {
                            category,
                            images: [],
                        };

                        images.push(list);
                    }

                    let url = image.custom?.image;

                    if (!url.endsWith('.svg')) {
                        url += '.svg';
                    }

                    list.images.push({
                        url,
                        category: names[image.custom?.category] ?? image.custom?.category,
                        name: this.viewController.componentNames(image).join(''),
                    });
                });

                return images;
            },

            /**
             * Figure out a list of available colors.
             */
            availableColors() {
                return this.$store.getters.availablePersonalizationColors;
            },

            /**
             * Get a list of primary colors to be shown in the plain list.
             */
            primaryColors() {
                const primaries = [];

                if (this.lastSelectedNonprimary) {
                    primaries.push(this.lastSelectedNonprimary);
                }

                primaries.push(...this.availableColors.filter((c) => {
                    if (c.custom && c.custom['v-primary-color'] === 'true') {
                        return true;
                    }

                    return false;
                }));

                return primaries;
            },

            /**
             * Figure out the selected color caption.
             */
            selectedColor() {
                if (this.editPlacement) {
                    const selected = this.viewController.selectedAtPlacement(this.editPlacement.code);

                    if (selected && selected.custom && selected.custom['effect-colorize-name']) {
                        const color = {
                            name: selected.custom['effect-colorize-name'],
                            hex: selected.custom['effect-colorize-add'],
                            custom: {
                                hex: selected.custom['effect-colorize-add'],
                            },
                        };

                        const available = this.availableColors.find((c) => c.custom.hex === color.hex);
                        if (available && available.custom['v-primary-color'] === 'true') {
                            color.primary = true;
                        }

                        return color;
                    }
                }

                return null;
            },

            /**
             * Figure out the selected color CSS style.
             */
            selectedColorStyle() {
                const css = {};

                if (this.editPlacement) {
                    const selected = this.viewController.selectedAtPlacement(this.editPlacement.code);

                    if (selected && selected.custom && selected.custom['effect-colorize-add']) {
                        const hex = selected.custom['effect-colorize-add'];

                        css.backgroundColor = hex.startsWith('#') ? hex : `#${hex}`;
                    }
                }

                return css;
            },

            /**
             * Can the design be wrapped fully around the bottle.
             */
            canFullWrap() {
                if (this.viewController) {
                    return this.viewController.blueprint.custom['v-design-wrap'] === 'true';
                }

                return false;
            },
        },
        watch: {
            /**
             * Saves the last selected non primary color so it could be shown in the quick picker.
             */
            selectedColor() {
                if (this.selectedColor && !this.selectedColor.primary) {
                    this.lastSelectedNonprimary = this.selectedColor;
                }
            },

            /**
             * Move the design if current view changes.
             */
            currentView() {
                this.moveDesign();
            },
        },
        mounted() {
            // When the component is loaded, capture the current state of the view controller and save it in case
            // we need to revert to the previous state and toss away all customizations.
            //
            // At any given time there should be only one module that can do the revert.
            this.captureController();

            if (this.filledImagePlacements.length === 0) {
                if (this.emptyImagePlacements.length > 0) {
                    this.mode = 'upload';
                    this.uploadMode = 'image';
                }
            }

            if (this.selectedColor && !this.selectedColor.primary) {
                this.lastSelectedNonprimary = this.selectedColor;
            }
            fillEmptyDivs();
        },
        beforeDestroy() {
            // Reenable swiping.
            this.$store.dispatch('lockSwipe', false);
            this.$store.dispatch('editPlacement', null);
            this.editPlacement = null;
        },
        methods: {
            ...mapActions({
                selectStep: 'selectStep',
                setViewController: 'showController',
                showGenericConfirm: 'showGenericConfirm',
            }),
            /**
             * Captures the current state and active controller so we could cancel changes if necessary.
             */
            captureController() {
                if (!this.viewController) { return; }
                this.backupViewController = this.viewController.clone({ excludeEvents: false });
            },
            goToMainMenu() {
                this.selectStep(null);
            },
            revertSelections() {
                this.setViewController(this.backupViewController);
            },
            accept() {
                this.goToMainMenu();
            },
            cancel() {
                this.showGenericConfirm({
                    title: this.$t('CANCEL_CONFIRMATION'),
                    description: this.$t('CANCEL_CONFIRMATION_BODY'),
                    cancelAction: this.$t('CANCEL_CONFIRMATION_CANCEL'),
                    confirmAction: this.$t('CANCEL_CONFIRMATION_OK'),
                    confirmationCallback: () => {
                        this.revertSelections();
                        this.goToMainMenu();
                    },
                });
            },
            cancelImageUpload() {
                if (this.filledImagePlacements.length > 0) {
                    this.mode = 'list';
                } else {
                    this.cancel();
                }
            },
            /**
             * A dragged file had entered the drop area.
             */
            uploaderDragEnter() {
                this.uploaderDraggingOver = true;
            },
            /**
             * Dragging a file over the drop area.
             */
            uploaderDragOver(e) {
                e.preventDefault();
            },

            /**
             * Leaving the drag area.
             */
            uploaderDragLeave() {
                this.uploaderDraggingOver = false;
            },

            /**
             * A file was dropped on the upload area.
             */
            uploaderDrop(e) {
                e.preventDefault();

                this.uploaderDraggingOver = false;

                if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    if (this.emptyImagePlacements.length > 0) {
                        this.handleFileImage(e.dataTransfer.files[0], {
                            targetPlacement: this.emptyImagePlacements[0],
                        });
                    }
                }
            },

            /**
             * Triggers file selection.
             */
            pickFile() {
                this.$refs.uploaderFullSource.click();
            },

            /**
             * Picked a file.
             */
            uploaderPicked() {
                if (this.$refs.uploaderFullSource.files.length > 0) {
                    if (this.emptyImagePlacements.length > 0) {
                        this.handleFileImage(this.$refs.uploaderFullSource.files[0], {
                            targetPlacement: this.emptyImagePlacements[0],
                        });
                    }

                    this.$refs.uploaderFullSource.value = null;
                }
            },

            /**
             * Handle image upload.
             */
            handleFileImage(file, options) {
                return new Promise((resolve) => {
                    // Validate the file type.

                    if (file instanceof File) {
                        if (!this.mimeTypesSelected.includes(file.type)) {
                            if (this.personalizationMethod === constants.METHOD_PRINTING) {
                                this.$store.dispatch('showGenericNotify', {
                                    title: this.$t('IMAGE_UPLOAD_BAD_TYPE_PRINTING'),
                                    description: this.$t('IMAGE_UPLOAD_BAD_TYPE_PRINTING_COPY'),
                                    action: this.$t('ACTION_CONTINUE'),
                                });
                            } else {
                                this.$store.dispatch('showGenericNotify', {
                                    title: this.$t('IMAGE_UPLOAD_BAD_TYPE_ETCHING'),
                                    description: this.$t('IMAGE_UPLOAD_BAD_TYPE_ETCHING_COPY'),
                                    action: this.$t('ACTION_CONTINUE'),
                                });
                            }

                            return;
                        }

                        if (this.personalizationMethod === 'printing') {
                            if (file.type.includes('jpeg')) {
                                this.$store.dispatch('showGenericNotify', {
                                    title: this.$t('IMAGE_UPLOAD_JPEG'),
                                    description: this.$t('IMAGE_UPLOAD_JPEG_COPY'),
                                    action: this.$t('ACTION_CONTINUE'),
                                });
                            }
                        }
                    }

                    const formData = new FormData();

                    if (file instanceof File) {
                        formData.append('file', file);
                    } else {
                        formData.append('copy-from', file);
                    }

                    const target = options.targetPlacement.component.custom;

                    const imageMinHeight = target && target['dynamic-image-upload-limit-min-height'] ? +target['dynamic-image-upload-limit-min-height'] : null;
                    const imageMaxHeight = target && target['dynamic-image-upload-limit-max-height'] ? +target['dynamic-image-upload-limit-max-height'] : null;

                    const imageMinWidth = target && target['dynamic-image-upload-limit-min-width'] ? +target['dynamic-image-upload-limit-min-width'] : null;
                    const imageMaxWidth = target && target['dynamic-image-upload-limit-max-width'] ? +target['dynamic-image-upload-limit-max-width'] : null;

                    if (imageMinHeight && imageMinWidth) {
                        formData.append('min-width', imageMinWidth);
                        formData.append('min-height', imageMinHeight);
                    }

                    if (imageMaxHeight && imageMaxWidth) {
                        formData.append('max-width', imageMaxWidth);
                        formData.append('max-height', imageMaxHeight);
                    }

                    const fit = target && target['dynamic-image-upload-fit'] ? +target['dynamic-image-upload-fit'] : null;
                    const fitWidth = target && target['dynamic-image-upload-fit-width'] ? +target['dynamic-image-upload-fit-width'] : null;
                    const fitHeight = target && target['dynamic-image-upload-fit-height'] ? +target['dynamic-image-upload-fit-height'] : null;

                    if (fit && fitWidth && fitHeight) {
                        formData.append('fit', fit);
                        formData.append('fit-width', fitWidth);
                        formData.append('fit-height', fitHeight);
                    }

                    formData.append('min-raster-width', constants.MIN_RASTER_WIDTH);
                    formData.append('min-raster-height', constants.MIN_RASTER_HEIGHT);

                    if (this.personalizationMethod === 'etching') {
                        let trace = true;

                        if (typeof file === 'string' && file.includes('.svg')) {
                            trace = false;
                        }

                        if (trace) {
                            formData.append('trace', 'true');
                            formData.append('trace-threshold', '0.5');
                            formData.append('bgc', 'ffffff');
                            //     'min-raster-width': 2000,
                            //     'min-raster-height': 2000,
                            //     'jpeg-preview-size': 500,
                            //     'jpeg-preview-background': '255,255,255,255',
                            // });
                        }
                    }

                    if (options && options.type === 'gallery') {
                        formData.append('allow-reuse', 'true');
                    }

                    this.uploadWorking = true;
                    this.uploadProgress = 0;

                    axios({
                        method: 'post',
                        url: '//api.images.drivecommerce.com/api/v1/image/logomark/upload/',
                        data: formData,
                        config: {
                            headers: { 'Content-Type': 'multipart/form-data' },
                        },
                        onUploadProgress: (progressEvent) => {
                            this.uploadProgress = (progressEvent.loaded / progressEvent.total) * 100;
                        },
                    }).then((response) => {
                        // debugger;
                        this.uploadWorking = false;

                        resolve(this.handleImageResponse(response, file, options));
                    }).catch((error) => {
                        this.uploadWorking = false;

                        if (error && error.response && error.response.data) {
                            const { data } = error.response;

                            if (data.modelState && data.modelState[''] && data.modelState[''].length > 0) {
                                if (data.details && data.details.error === 'maximum-dimensions') {
                                    this.$store.dispatch('showGenericNotify', {
                                        title: this.$t('IMAGE_UPLOAD_BAD_SIZE'),
                                        description: this.$t('IMAGE_UPLOAD_BAD_SIZE_COPY'),
                                        action: this.$t('ACTION_CONTINUE'),
                                    });
                                } else if (data.details && data.details.error === 'font-outlines') {
                                    this.$store.dispatch('showGenericNotify', {
                                        title: this.$t('IMAGE_UPLOAD_BAD_OUTLINES'),
                                        description: this.$t('IMAGE_UPLOAD_BAD_OUTLINES_COPY'),
                                        action: this.$t('ACTION_CONTINUE'),
                                    });
                                } else {
                                    this.$store.dispatch('toast', {
                                        message: data.modelState[''][0],
                                        type: 'error',
                                    });
                                }
                            }
                        }
                    });
                });
            },

            /**
             * Handles image response.
             */
            handleImageResponse(response, file, {
                type,
                targetPlacement,
                previewOnly,
                name,
                category,
            }) {
                this.$store.dispatch('validateEditableViews');
                this.$store.dispatch('switchToCommonView');

                const image = response.data;

                // Pick a placement to put image to.
                const { placement, component } = targetPlacement;

                this.$store.dispatch('takeEmptyPlacement', placement);

                const { view } = this.currentView;

                const designOn = view.custom['v-design-area'];

                const {
                    left, right, top, bottom,
                } = designArea(this.$store.state.customizer, view.custom['v-design-area']);

                if (left != null && right != null && top != null && bottom != null) {
                    // Figure out initial scaling to fit into the design area.
                    const areaWidth = right - left;
                    const areaHeight = bottom - top;

                    this.viewController.commitCustomAttributes(placement.code, {
                        'dynamic-image-area-width': areaWidth.toString(),
                        'dynamic-image-area-height': areaHeight.toString(),
                    });
                }

                // Configure the image.
                this.viewController.updateDynamicImage(placement.code, {
                    title: image.title,
                    name: image.name,
                    url: image.imageUrl,
                    pngUrl: image.pngImageUrl,
                    jpgUrl: image.jpgImageUrl,
                    width: image.width,
                    height: image.height,
                });

                this.$store.dispatch('renderOnlyOnView', {
                    view: this.currentView,
                });

                const group = this.viewController.blueprint.custom[`v-group-${view.custom['v-design-area']}`];

                this.$store.dispatch('setGroup', { placement: placement.code, group });

                // Update dynamic image will center the image; zero out coordinates.
                this.viewController.commitCustomAttributes(placement.code, {
                    'v-design-on': view.custom['v-design-area'],
                    mask: view.custom['v-design-mask'],
                    'dynamic-image-x': 0,
                    'dynamic-image-y': 0,
                    'effect-colorize': null,
                    'effect-colorize-add': null,
                    'effect-colorize-name': null,
                });

                // ... and center manually in the current view.
                if (left != null && right != null && top != null && bottom != null) {
                    // Figure out initial scaling to fit into the design area.
                    const areaWidth = right - left;
                    const areaHeight = bottom - top;

                    const imageWidth = +component.custom['dynamic-image-width'];
                    const imageHeight = +component.custom['dynamic-image-height'];

                    const fitScaleWidth = areaWidth / imageWidth;
                    const fitScaleHeight = areaHeight / imageHeight;

                    const fitScale = Math.min(fitScaleWidth, fitScaleHeight);

                    this.viewController.commitCustomAttributes(placement.code, {
                        'dynamic-image-offset-x': ((left + right) * 0.5),
                        'dynamic-image-offset-y': ((top + bottom) * 0.5),
                        'dynamic-image-scale': fitScale.toString(),
                    });
                }

                if (type === 'gallery') {
                    Vue.set(component.custom, 'gallery-image', 'true');
                    if (name) {
                        Vue.set(component.custom, 'gallery-image-name', name);
                    } else {
                        Vue.set(component.custom, 'gallery-image-name', null);
                    }

                    if (category) {
                        Vue.set(component.custom, 'gallery-image-category', category);
                    } else {
                        Vue.set(component.custom, 'gallery-image-category', null);
                    }
                } else {
                    Vue.set(component.custom, 'gallery-image', 'false');
                }

                if (file instanceof File) {
                    Vue.set(component.custom, 'dynamic-image-original-name', file.name);
                }

                if (!previewOnly) {
                    this.beginEdit(placement, component);
                }

                return component;
            },

            /**
             * Enters image editing mode.
             */
            beginEdit(placement, component) {
                this.undoEditController = this.viewController.clone({ excludeEvents: false });

                this.previewPlacement = null;
                this.editPlacement = placement;
                this.editComponent = component;
                this.mode = 'edit';

                this.$store.dispatch('lockSwipe', true);
                this.$store.dispatch('editPlacement', placement);

                if (component.custom['v-design-on']) {
                    this.$store.dispatch('switchToDesignView', component.custom['v-design-on']);
                }

                setTimeout(() => {
                    this.$store.dispatch('setCurrentTour', Tours.edit());
                }, 250);
            },

            /**
             * Selects upload mode.
             */
            selectUploadMode(mode) {
                if (mode === 'gallery') {
                    // Take a snapshot of the current controller.
                    this.revertGalleryController = this.viewController.clone({ excludeEvents: false });
                    // eslint-disable-next-line
                    this.previewPlacement = this.emptyImagePlacements[0];
                }

                if (mode === 'image') {
                    // If returning from the gallery and the image was not committed, revert the controller.
                    if (this.revertGalleryController) {
                        this.setViewController(this.revertGalleryController);
                    }
                }

                this.uploadMode = mode;
                this.selectedGalleryImage = null;
            },

            /**
             * Cancels gallery selection.
             */
            cancelGallery() {
                // If returning from the gallery and the image was not committed, revert the controller.
                if (this.revertGalleryController) {
                    this.setViewController(this.revertGalleryController);
                }

                this.mode = 'list';
            },

            /**
             * Accepts gallery selection.
             */
            acceptGallery() {
                this.uploadGalleryImageInternal();
                this.revertGalleryController = null;
            },

            /**
             * Select gallery image.
             */
            selectGalleryImage(image) {
                this.selectedGalleryImage = image;

                if (image) {
                    this.uploadGalleryImageInternal(true);
                }
            },

            /**
             * Uploads the selected gallery image.
             */
            uploadGalleryImage() {
                this.uploadGalleryImageInternal();
                this.revertGalleryController = null;
            },

            uploadGalleryImageInternal(previewOnly) {
                if (this.selectedGalleryImage && this.previewPlacement) {
                    let { url } = this.selectedGalleryImage;
                    const { flags } = this.selectedGalleryImage;

                    if (url.startsWith('//')) {
                        url = `https:${url}`;
                    }

                    this.handleFileImage(url, {
                        type: 'gallery',
                        category: this.selectedGalleryImage.category,
                        name: this.selectedGalleryImage.name,
                        previewOnly,
                        targetPlacement: this.previewPlacement,
                    }).then((component) => {
                        Vue.set(component.custom, 'gallery-flags', flags);
                    });
                }
            },

            /**
             * Cancels image editing.
             */
            cancelEdit() {
                if (this.undoEditController) {
                    this.setViewController(this.undoEditController);

                    this.undoEditController = null;
                }

                this.mode = 'list';

                this.$store.dispatch('lockSwipe', false);
                this.$store.dispatch('editPlacement', null);
                this.editPlacement = null;
            },

            /**
             * Saves image edits.
             */
            saveEdit() {
                if (this.mode === 'list') {
                    this.$store.dispatch('selectStep', null);
                } else {
                    this.mode = 'list';

                    this.$store.dispatch('lockSwipe', false);
                    this.$store.dispatch('editPlacement', null);
                    this.editPlacement = null;

                    setTimeout(() => {
                        this.$store.dispatch('setCurrentTour', Tours.list());
                    }, 250);
                }
            },

            /**
             * Change image.
             */
            changeImage() {
                this.$refs.uploaderEditSource.click();
            },

            /**
             * Picked a replacement file.
             */
            editUploaderPicked() {
                if (this.$refs.uploaderEditSource.files.length > 0) {
                    this.handleFileImage(this.$refs.uploaderEditSource.files[0], {
                        targetPlacement: {
                            placement: this.editPlacement,
                            component: this.editComponent,
                        },
                    });

                    this.$refs.uploaderEditSource.value = null;
                }
            },

            /**
             * Removes uploaded image.
             */
            removeImage() {
                this.$store.dispatch('clearPlacement', {
                    placement: this.editPlacement,
                });

                if (this.filledImagePlacements.length === 0) {
                    this.accept();
                } else {
                    this.mode = 'list';

                    this.$store.dispatch('lockSwipe', false);
                    this.$store.dispatch('editPlacement', null);
                    this.editPlacement = null;
                }
            },

            /**
             * Align the image to the left.
             */
            alignLeft() {
                if (this.currentView) {
                    const { left } = designArea(this.$store.state.customizer, this.editComponent.custom['v-design-on']);

                    if (left != null) {
                        const { dx } = extent(this.editPlacement, this.editComponent);

                        this.viewController.commitCustomAttributes(this.editPlacement.code, {
                            'dynamic-image-offset-x': left + dx,
                        });
                    }
                }
            },

            /**
             * Align the image to the center.
             */
            alignCenter() {
                if (this.currentView) {
                    const { left, right } = designArea(this.$store.state.customizer, this.editComponent.custom['v-design-on']);

                    if (left != null && right != null) {
                        this.viewController.commitCustomAttributes(this.editPlacement.code, {
                            'dynamic-image-offset-x': ((left + right) * 0.5),
                        });
                    }
                }
            },

            /**
             * Align the image to the right.
             */
            alignRight() {
                if (this.currentView) {
                    const { right } = designArea(this.$store.state.customizer, this.editComponent.custom['v-design-on']);

                    if (right != null) {
                        const { dx } = extent(this.editPlacement, this.editComponent);

                        this.viewController.commitCustomAttributes(this.editPlacement.code, {
                            'dynamic-image-offset-x': right - dx,
                        });
                    }
                }
            },

            /**
             * Align the image to the top.
             */
            alignTop() {
                if (this.currentView) {
                    const { top } = designArea(this.$store.state.customizer, this.editComponent.custom['v-design-on']);

                    if (top != null) {
                        const { dy } = extent(this.editPlacement, this.editComponent);

                        this.viewController.commitCustomAttributes(this.editPlacement.code, {
                            'dynamic-image-offset-y': top + dy,
                        });
                    }
                }
            },

            /**
             * Align the image to the center.
             */
            alignVCenter() {
                if (this.currentView) {
                    const { top, bottom } = designArea(this.$store.state.customizer, this.editComponent.custom['v-design-on']);

                    if (top != null && bottom != null) {
                        this.viewController.commitCustomAttributes(this.editPlacement.code, {
                            'dynamic-image-offset-y': (top + bottom) * 0.5,
                        });
                    }
                }
            },

            /**
             * Align the image to the bottom.
             */
            alignBottom() {
                if (this.currentView) {
                    const { bottom } = designArea(this.$store.state.customizer, this.editComponent.custom['v-design-on']);

                    if (bottom != null) {
                        const { dy } = extent(this.editPlacement, this.editComponent);

                        this.viewController.commitCustomAttributes(this.editPlacement.code, {
                            'dynamic-image-offset-y': bottom - dy,
                        });
                    }
                }
            },

            /**
             * Move the current image up a layer.
             */
            layerUp() {
                this.$store.dispatch('layerUp', this.editPlacement);
            },

            /**
             * Move the current image down a layer.
             */
            layerDown() {
                this.$store.dispatch('layerDown', this.editPlacement);
            },

            /**
             * Begins adding a new image.
             */
            listAddImage() {
                this.mode = 'upload';
                this.uploadMode = 'image';
                this.selectedGalleryImage = null;
            },

            /**
             * Removes an image.
             */
            listRemoveImage(image) {
                this.$store.dispatch('clearPlacement', image);

                if (this.filledImagePlacements.length === 0) {
                    this.accept();
                }
            },

            /**
             * Edits an image.
             */
            listEditImage(image) {
                this.beginEdit(image.placement, image.component);
            },

            /**
             * Edit image layering.
             */
            editLayering() {
                this.editMode = 'layering';
            },

            /**
             * Edit image alignment.
             */
            editAlignment() {
                this.editMode = 'alignment';
            },

            /**
             * Back to editing menu.
             */
            backToEdit() {
                this.editMode = null;
            },

            uploadProgressStyle() {
                const css = {};

                const end = 90 + ((360 * this.uploadProgress) / 100);

                if (this.uploadProgress <= 50) {
                    css.backgroundImage = `linear-gradient(450deg, #ccc 50%, transparent 50%), linear-gradient(${end}deg, #000 50%, transparent 50%)`;
                } else {
                    css.backgroundImage = `linear-gradient(${end}deg, #000 50%, transparent 50%), linear-gradient(270deg, #000 50%, transparent 50%)`;
                }

                return css;
            },

            /**
             * Begin selecting a color.
             */
            selectColor() {
                this.revertColor = this.selectedColor;

                this.mode = 'color';
            },

            /**
             * Get color style.
             */
            getColorStyle(option) {
                const css = {};

                if (option.custom && option.custom.hex) {
                    const { hex } = option.custom;

                    css.backgroundColor = hex.startsWith('#') ? hex : `#${hex}`;
                }

                return css;
            },

            /**
             * Update the color selection.
             */
            pickColor(color) {
                this.viewController.commitCustomAttributes(this.editPlacement.code, {
                    'effect-colorize': '000000',
                    'effect-colorize-add': color.custom.hex,
                    'effect-colorize-name': color.name,
                });
            },

            /**
             * Accepts color selection.
             */
            acceptColor() {
                this.mode = 'edit';
            },

            /**
             * Revert color selection.
             */
            cancelColor() {
                if (this.revertColor) {
                    this.viewController.commitCustomAttributes(this.editPlacement.code, {
                        'effect-colorize': '000000',
                        'effect-colorize-add': this.revertColor.hex,
                        'effect-colorize-name': this.revertColor.name,
                    });
                } else {
                    this.viewController.commitCustomAttributes(this.editPlacement.code, {
                        'effect-colorize': null,
                        'effect-colorize-add': null,
                        'effect-colorize-name': null,
                    });
                }

                this.mode = 'edit';
            },

            /**
             * Edit text color on small screens.
             */
            editSmallColor() {
                this.revertColor = this.selectedColor;

                this.editMode = 'color';
            },

            /**
             * Edit text color.
             */
            editColor() {
                this.revertColor = this.selectedColor;

                this.mode = 'color';
            },

            /**
             * Pick gallery flyout mode.
             */
            pickGallery() {
                if (this.emptyImagePlacements.length > 0) {
                    this.mode = 'gallery';

                    this.selectedGalleryImage = null;

                    // Take a snapshot of the current controller.
                    this.revertGalleryController = this.viewController.clone({ excludeEvents: false });
                    // eslint-disable-next-line
                    this.previewPlacement = this.emptyImagePlacements[0];
                }
            },

            /**
             * Moves the current design to another surface.
             */
            moveDesign() {
                if (((this.editPlacement && this.editComponent) || (this.previewPlacement)) && this.currentView) {
                    const viewDesignArea = this.currentView.view.custom['v-design-area'];

                    const component = this.editComponent || this.viewController.selectedAtPlacement(this.previewPlacement.placement.code);
                    const placement = this.editPlacement || this.previewPlacement.placement;

                    const designOn = component.custom['v-design-on'];

                    if (viewDesignArea !== designOn) {
                        this.$store.dispatch('renderOnlyOnView', {
                            view: this.currentView,
                        });

                        const {
                            left, right, top, bottom,
                        } = designArea(this.$store.state.customizer, viewDesignArea);

                        if (left != null && right != null && top != null && bottom != null) {
                            const group = this.viewController.blueprint.custom[`v-group-${viewDesignArea}`];

                            this.$store.dispatch('setGroup', { placement: placement.code, group });

                            this.viewController.commitCustomAttributes(placement.code, {
                                'v-design-on': viewDesignArea,
                                mask: this.currentView.view.custom['v-design-mask'],
                                'dynamic-image-offset-x': ((left + right) * 0.5),
                                'dynamic-image-offset-y': ((top + bottom) * 0.5),
                                'dynamic-image-area-width': (right - left).toString(),
                                'dynamic-image-area-height': (bottom - top).toString(),
                            });
                        }
                    }
                }
            },

            makeImageUrl(u) {
                return `url("${u}")`;
            },
        },
    };
</script>
